/**
 * Firebase Authentication Providers
 *
 * Configures authentication providers for Firebase.
 * Currently supports:
 * - Microsoft (Entra ID) via OAuthProvider
 * - Email/Password (built-in)
 *
 * The Microsoft provider is configured to use the organisation's
 * Entra ID tenant for SSO.
 */

import { OAuthProvider } from 'firebase/auth';
import { GoogleAuthProvider } from 'firebase/auth';

/**
 * Microsoft OAuth Provider
 *
 * Configured to use the organisation's Entra ID tenant.
 * Falls back to 'common' tenant for multi-tenant scenarios.
 */
export const microsoftProvider = new OAuthProvider('microsoft.com');

// Configure Microsoft provider with tenant-specific settings
const _msTenantId = import.meta.env.VITE_ENTRA_TENANT_ID || 'common';
console.info('[AuthProviders] Microsoft provider tenant:', _msTenantId);

microsoftProvider.setCustomParameters({
  // Use the specific tenant or 'common' for any Microsoft account
  tenant: _msTenantId,
  // Always show account selection
  prompt: 'select_account',
});

// Add scopes for user profile access
microsoftProvider.addScope('openid');
microsoftProvider.addScope('profile');
microsoftProvider.addScope('email');

/**
 * Google OAuth Provider
 */
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});
googleProvider.addScope('openid');
googleProvider.addScope('profile');
googleProvider.addScope('email');

/**
 * Provider type identifiers
 */
export type AuthProviderType = 'microsoft' | 'google' | 'email';

/**
 * Check if a provider type is valid
 */
export function isValidProvider(provider: string): provider is AuthProviderType {
  return provider === 'microsoft' || provider === 'google' || provider === 'email';
}
